import { t as tr } from '../i18n.js'
import { Section, jumpToSection } from '../section.js'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CopyButton } from '../copy.js'
import { api, ApiError, type DirectoryUser, type SessionInfo } from '../api.js'
import { useAuth } from '../auth.js'
import { SecurityPanel } from './Security.js'
import { LOCALES } from '../i18n.js'

export function AccountPage() {
  const { user, refresh, signOut } = useAuth()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC')
  const [jobTitle, setJobTitle] = useState(user?.jobTitle ?? '')
  const [githubLogin, setGithubLogin] = useState(user?.githubLogin ?? '')
  const [locale, setLocale] = useState(user?.locale ?? 'en')
  const [profileMsg, setProfileMsg] = useState<string | null>(null)

  const profile = useMutation({
    mutationFn: () => api<{ user: DirectoryUser }>('/users/me', { method: 'PATCH', json: { displayName, timezone, jobTitle: jobTitle || null, locale, githubLogin: githubLogin.trim() || null } }),
    onSuccess: async () => { setProfileMsg('Saved.'); await refresh() },
    onError: (e) => setProfileMsg(e instanceof ApiError ? e.message : 'Could not save'),
  })

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwMsg, setPwMsg] = useState<string | null>(null)
  const password = useMutation({
    mutationFn: () => api('/auth/password', { method: 'POST', json: { currentPassword: current || undefined, newPassword: next } }),
    onSuccess: async () => { setPwMsg('Password updated. Other sessions were signed out.'); setCurrent(''); setNext(''); setConfirmPw(''); await refresh(); await qc.invalidateQueries({ queryKey: ['sessions'] }) },
    onError: (e) => setPwMsg(e instanceof ApiError ? e.message : 'Could not change password'),
  })

  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api<{ current: string; sessions: SessionInfo[] }>('/auth/sessions') })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })
  const revokeAll = useMutation({
    mutationFn: () => api('/auth/logout-all', { method: 'POST', json: { keepCurrent: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })

  const onProfile = (e: FormEvent) => { e.preventDefault(); setProfileMsg(null); profile.mutate() }
  const onPassword = (e: FormEvent) => { e.preventDefault(); setPwMsg(null); if (next !== confirmPw) { setPwMsg('The two passwords do not match.'); return } password.mutate() }

  return (
    <>
      <h1>{tr("Account")}</h1>
      <p className="muted">{user?.email} · {user?.role}</p>

      <Section id="acct-profile" title="Profile" subtitle={tr("name, language, timezone")} defaultOpen>
        
        <form onSubmit={onProfile} className="stack">
          <label>{tr("Name")}<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={200} /></label>
          <label>{tr("Job title")}<input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} maxLength={200} /></label>
          <label>{tr("GitHub username")}<input value={githubLogin} onChange={(e) => setGithubLogin(e.target.value)} maxLength={39} placeholder="octocat" /><span className="hint">{tr("Issues assigned to this GitHub user become tasks assigned to you when a project imports them.")}</span></label>
          <label>{tr("Language")}<select value={locale} onChange={(e) => setLocale(e.target.value)}>{LOCALES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}</select><span className="hint">{tr("Applies immediately to this browser and every device you sign in on; overrides the browser language.")}</span></label>
          <label>{tr("Timezone")}<input value={timezone} onChange={(e) => setTimezone(e.target.value)} required />
            <span className="hint">{tr("Display only — never changes which week an entry belongs to.")}</span></label>
          {profileMsg && <p className={profile.isError ? 'bad' : 'good'}>{profileMsg}</p>}
          <button className="button" disabled={profile.isPending}>{tr("Save")}</button>
        </form>
      </Section>

      <EmailPanel />

      <Section id="acct-password" title={user?.hasPassword ? tr("Change password") : tr("Set a password")} subtitle={tr("current + new + confirm")}>
        
        {!user?.hasPassword && <p className="muted small">{tr("You sign in with Google. A password gives you a fallback if Google is unavailable.")}</p>}
        <form onSubmit={onPassword} className="stack">
          {user?.hasPassword && (
            <label>{tr("Current password")}<input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></label>
          )}
          <label>{tr("New password")}<input type="password" autoComplete="new-password" minLength={12} maxLength={256} value={next} onChange={(e) => setNext(e.target.value)} required /></label>
          <label>{tr("Confirm new password")}<input type="password" autoComplete="new-password" minLength={12} maxLength={256} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required aria-invalid={confirmPw !== '' && confirmPw !== next} />
            {confirmPw !== '' && confirmPw !== next && <span className="hint bad">{tr("Does not match.")}</span>}</label>
          {pwMsg && <p className={password.isError || pwMsg.includes('match') ? 'bad' : 'good'}>{pwMsg}</p>}
          <button className="button" disabled={password.isPending}>{tr("Update password")}</button>
        </form>
      </Section>

      <SecurityPanel />

      <IcalPanel />
      <CalendarPanel />

      <Section id="acct-export" title="Your data" subtitle={tr("download everything stored about you")}>
        
        <p className="small muted">{tr("Download everything stored about you — profile, memberships, time entries, tasks assigned, leave, approvals, notifications — as JSON.")}</p>
        <div><a className="button" href="/api/me/export.json" data-tip={tr("Download a JSON file with everything stored about you")}>{tr("Download my data")}</a></div>
      </Section>

      <section className="card stack">
        <h2>{tr("Sign out")}</h2>
        <div><button className="button" data-tip={tr("End this session and return to the sign-in page (other devices stay signed in)")} onClick={async () => { await signOut(); nav('/login', { replace: true }) }}>Sign out</button></div>
      </section>

      <DeleteAccountPanel />

      <Section id="acct-sessions" title="Sessions" subtitle={tr("devices signed in as you")}>
        
        {sessions.data && (
          <table className="table">
            <thead><tr><th>{tr("Last active")}</th><th>{tr("Signed in")}</th><th>{tr("From")}</th><th></th></tr></thead>
            <tbody>
              {sessions.data.sessions.map((s) => (
                <tr key={s.id}>
                  <td>{fmt(s.lastSeenAt)}{s.id === sessions.data.current && <span className="tag">{tr("this device")}</span>}</td>
                  <td>{fmt(s.createdAt)}</td>
                  <td className="muted small">{s.ip ?? '—'}<br />{shortUa(s.userAgent)}</td>
                  <td>{s.id !== sessions.data.current && <button className="link" onClick={() => revoke.mutate(s.id)}>Sign out</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div><button className="button" onClick={() => revokeAll.mutate()} disabled={revokeAll.isPending}>Sign out everywhere else</button></div>
      </Section>
    </>
  )
}

export const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString() : '—'
const shortUa = (ua: string | null) => {
  if (!ua) return ''
  const m = ua.match(/(Firefox|Chrome|Safari|Edg)\/[\d.]+/)
  return m ? m[0] : ua.slice(0, 40)
}

function IcalPanel() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['ical'], queryFn: () => api<{ token: string | null; personalUrl: string | null }>('/ical/token') })
  const gen = useMutation({ mutationFn: () => api('/ical/token', { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['ical'] }) })
  return (
    <Section id="acct-ical" title="Calendar feed" subtitle={tr("iCal subscription link")}>
      
      <ul className="help small muted">
        <li>{tr("Subscribe your calendar app to this link to see your tasks (due dates), leave, your projects' milestones and your last 90 days of time entries.")}</li>
        <li>{tr("The link is a secret — anyone holding it can read your feed. Regenerate it if it leaks.")}</li>
        <li>{tr("Per-project feeds:")} <code>…/api/ical/&lt;token&gt;/project/&lt;project id&gt;.ics</code>.</li>
      </ul>
      {q.data?.personalUrl ? <code className="wrap">{q.data.personalUrl}</code> : <p className="muted small">{tr("No feed link yet.")}</p>}
      <div className="row"><button className="button" onClick={() => { if (!q.data?.token || confirm('Regenerate? Calendar apps using the old link will stop updating.')) gen.mutate() }}>{q.data?.token ? tr("Regenerate link") : tr("Create feed link")}</button>{q.data?.personalUrl && <CopyButton text={q.data.personalUrl} tip="Copy the feed URL for your calendar app" className="link" />}</div>
    </Section>
  )
}

function CalendarPanel() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['calendar-status'], queryFn: () => api<{ available: boolean; connected: boolean; email: string | null; connectedAt: string | null }>('/calendar/status') })
  const disc = useMutation({ mutationFn: () => api('/calendar/google/disconnect', { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-status'] }) })
  const flag = new URLSearchParams(location.search).get('calendar')
  if (!q.data?.available) return null
  return (
    <Section id="acct-gcal" title="Google Calendar" subtitle={tr("two-way sync")}>
      
      {flag === 'failed' && <p className="bad small">{tr("Connecting failed. Try again.")}</p>}
      {flag === 'norefresh' && <p className="bad small">{tr("Google did not grant offline access — remove ADKOM PM under your Google account’s third-party access, then connect again.")}</p>}
      <p className="small muted">{tr("Read-only: today’s meetings appear as one-click suggestions on the Time page. Your entries flow back to any calendar through the")} <a href="#acct-ical" onClick={(e) => { e.preventDefault(); jumpToSection('acct-ical') }}>Calendar feed</a>.</p>
      {q.data.connected
        ? <div className="row"><span className="good">Connected{q.data.email ? ` as ${q.data.email}` : ''}</span><button className="link danger" onClick={() => disc.mutate()}>Disconnect</button></div>
        : <div><a className="button" href="/api/calendar/google/connect">{tr("Connect Google Calendar")}</a></div>}
    </Section>
  )
}

function DeleteAccountPanel() {
  const { user, signOut } = useAuth()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const chk = useQuery({ queryKey: ['delete-check'], queryFn: () => api<{ allowed: boolean; reasons: string[]; soleAdminOf: string[] }>('/me/delete-account/check'), enabled: open })
  const del = useMutation({
    mutationFn: () => api('/me/delete-account', { method: 'POST', json: { confirmName: typed } }),
    onSuccess: async () => { await signOut().catch(() => {}); nav('/login?error=deactivated', { replace: true }) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not delete the account'),
  })
  const name = user?.displayName ?? ''
  return (
    <Section id="acct-delete" title="Delete account" subtitle={tr("permanent — read first")}>
      
      {!open && <div><button className="button danger" data-tip={tr("Deactivate your account (history is kept); you will be asked to confirm")} onClick={() => setOpen(true)}>Delete my account…</button></div>}
      {open && (
        <div className="stack">
          <p className="bad"><strong>{tr("Read this first.")}</strong> Deleting your account signs you out everywhere and deactivates it: you will not be able to sign in again unless an administrator reactivates you. Your time entries, tasks, approvals and history are <strong>{tr("kept")}</strong> — they belong to the projects, not to you — and the administrators of your projects and the system administrators will be notified.</p>
          {chk.data && !chk.data.allowed && <p className="bad">Not possible yet: {chk.data.reasons.join('; ')}.</p>}
          {chk.data?.allowed && (
            <>
              <p>{tr("To confirm, type your name exactly:")} <code>{name}</code></p>
              <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={name} autoComplete="off" data-tip={tr("Type your display name exactly to enable the final button")} />
              <div className="row">
                <button className="button danger" disabled={typed.trim() !== name.trim() || del.isPending} data-tip={tr("Final step: deactivate the account now")} onClick={() => { setErr(null); del.mutate() }}>Yes, delete my account</button>
                <button className="button" onClick={() => { setOpen(false); setTyped('') }} data-tip={tr("Keep the account")}>Cancel</button>
              </div>
            </>
          )}
          {chk.data && !chk.data.allowed && <div><button className="button" onClick={() => setOpen(false)}>Close</button></div>}
          {err && <p className="bad small">{err}</p>}
        </div>
      )}
    </Section>
  )
}

/** Change sign-in email: immediately with password, or via a link mailed to the new address. Google-linked accounts cannot. */
function EmailPanel() {
  const qc = useQueryClient()
  const { refresh } = useAuth()
  const info = useQuery({ queryKey: ['me-email'], queryFn: () => api<{ email: string; googleLinked: boolean; pending: { newEmail: string; expiresAt: string } | null }>('/me/email') })
  const [mode, setMode] = useState<'now' | 'verify'>('verify')
  const [newEmail, setNewEmail] = useState('')
  const [pw, setPw] = useState('')
  const [msg, setMsg] = useState<{ kind: 'good' | 'bad'; text: string; url?: string } | null>(null)
  const change = useMutation({
    mutationFn: () => api<{ changed: boolean; emailed: boolean; verifyUrl: string | null; expiresAt: string | null }>('/me/email', { method: 'POST', json: { newEmail, mode, currentPassword: mode === 'now' ? pw : undefined } }),
    onSuccess: async (r) => {
      setPw('')
      if (r.changed) { setNewEmail(''); setMsg({ kind: 'good', text: tr("Changed. Use the new address next time you sign in.") }); await refresh() }
      else if (r.emailed) setMsg({ kind: 'good', text: tr("A confirmation link was sent to the new address (valid 2 days). Your email changes when you open it.") })
      else setMsg({ kind: 'good', text: tr("Mail is not configured on this server, so open this link yourself (valid 2 days):"), ...(r.verifyUrl ? { url: r.verifyUrl } : {}) })
      void qc.invalidateQueries({ queryKey: ['me-email'] })
    },
    onError: (e) => setMsg({ kind: 'bad', text: e instanceof ApiError ? e.message : 'Request failed' }),
  })
  const cancel = useMutation({ mutationFn: () => api('/me/email/pending', { method: 'DELETE' }), onSuccess: () => { setMsg(null); void qc.invalidateQueries({ queryKey: ['me-email'] }) } })
  const d = info.data
  return (
    <Section id="acct-email" title={tr("Email")} subtitle={d?.email ?? ''}>
      {d?.googleLinked ? (
        <p className="small muted">{tr("This account signs in with Google, so its email is managed by Google and cannot be changed here.")}</p>
      ) : (
        <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); setMsg(null); change.mutate() }}>
          <ul className="help small muted">
            <li>{tr("Your email is what you sign in with and where notifications are mailed.")}</li>
            <li><strong>{tr("Change now")}</strong> — {tr("enter your current password and the address changes immediately.")}</li>
            <li><strong>{tr("Verify first")}</strong> — {tr("a link is sent to the new address; the change happens when you open it, so a typo can't lock you out.")}</li>
            <li>{tr("Either way the old address is told, and the change is recorded in the audit log.")}</li>
          </ul>
          {d?.pending && <p className="notice small">{tr("Waiting for confirmation of")} <strong>{d.pending.newEmail}</strong> ({tr("expires")} {fmt(d.pending.expiresAt)}). <button type="button" className="link" onClick={() => cancel.mutate()}>{tr("Cancel it")}</button></p>}
          <label>{tr("New email")}<input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required maxLength={320} autoComplete="email" /></label>
          <div className="row" style={{ gap: '1rem' }}>
            <label className="inline"><input type="radio" name="emailmode" checked={mode === 'verify'} onChange={() => setMode('verify')} /> {tr("Verify first (send a link)")}</label>
            <label className="inline"><input type="radio" name="emailmode" checked={mode === 'now'} onChange={() => setMode('now')} /> {tr("Change now (with password)")}</label>
          </div>
          {mode === 'now' && <label>{tr("Current password")}<input type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} required /></label>}
          {msg && <p className={msg.kind}>{msg.text} {msg.url && <code style={{ wordBreak: 'break-all' }}>{msg.url}</code>}</p>}
          <div><button className="button" disabled={change.isPending || !newEmail} data-tip={mode === 'now' ? tr("Change the email immediately") : tr("Send a confirmation link to the new address")}>{mode === 'now' ? tr("Change email") : tr("Send confirmation link")}</button></div>
        </form>
      )}
    </Section>
  )
}
